import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateRowConfigDto } from '@asset-metrix/portal-gateway-client-js';
import {
  BasicMathPlugin,
  Button,
  ConditionalFilterPlugin,
  createChunks,
  ErrorMessage,
  fileReducer,
  FileSelection,
  GoogleFontIcon,
  jsonEditorWidgets,
  Modal,
  SVGIcon,
  Upload,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  SetOptionTitleToPlugin,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import clsx from 'clsx';

import { searchTableRowModalValues } from '../../api/portalGateway/searchTableRowModalValues';
import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { extractErrorMessage } from '../../utils/errorMessage';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import styles from './createRowModal.module.scss';

export type CreateRowModalProps = {
  config: CreateRowConfigDto;
  contentId: string;
  onApply: (
    files: File[],
    data: Record<string, unknown>
  ) => void | Promise<void>;
  onClose: (dirty?: boolean) => void;
  opened: boolean;
  instanceId: string;
  requestChunkSize: number;
};

export function CreateRowModal({
  onClose,
  opened,
  config,
  contentId,
  onApply,
  instanceId,
  requestChunkSize,
}: CreateRowModalProps) {
  const { t } = useTranslation();
  const [fileSelection, dispatch] = useReducer(fileReducer, []);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);
  const pageFilterSelections = useFilterSelections(instanceId);

  const cleanupAndClose = useCallback(
    (dirty: boolean) => {
      dispatch({ type: 'resetFileSelections' });
      setValidationErrors([]);
      onClose(dirty);
      setIsDirty(false);
    },
    [onClose]
  );

  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: config.jsonSchema ?? {},
    onChange: (data, _root, editor) => {
      setValidationErrors(editor.getErrors());
    },
    validate: false,
    addOptionalProps: true,
    plugins: [BasicMathPlugin, SetOptionTitleToPlugin],
  });

  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions: async (data: Record<string, unknown>) => {
      return await searchTableRowModalValues({
        contentId,
        data,
        context: { pageFilterSelections },
      });
    },
  });

  useEffect(() => {
    editor?.validate();
  }, [editor]);

  const isInitial = fileSelection.every(
    (selection) => selection.uploadState === 'initial'
  );

  const isPending = fileSelection.some(
    (selection) => selection.uploadState === 'isPending'
  );

  const upload = useCallback(
    async (
      fileSelections: FileSelection[],
      data: Record<string, unknown> = {}
    ) => {
      for (const fs of fileSelections) {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: fs.id, newState: 'isPending' },
        });
      }

      try {
        await onApply(
          fileSelections.map((selection) => selection.file),
          data.data ? data : {}
        );

        for (const fs of fileSelections) {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: { id: fs.id, newState: 'isSuccess' },
          });
        }
      } catch (e) {
        for (const fs of fileSelections) {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: {
              id: fs.id,
              newState: 'isError',
              error: e instanceof Error ? e.message : undefined,
            },
          });
        }
      }
    },
    [onApply]
  );

  const onSubmit = async () => {
    setIsDirty(true);
    setApiError(null);
    setSaving(true);

    const data = {
      data: editor?.getData(),
      context: { pageFilterSelections },
    };

    try {
      if (config.allowFileUpload) {
        const rows = config.oneRowPerFile
          ? fileSelection.map((fs) => [fs])
          : [fileSelection];

        for (const chunk of createChunks(rows, requestChunkSize)) {
          await Promise.all(chunk.map((c) => upload(c, data)));
        }
      } else {
        await onApply([], data);
        cleanupAndClose(true);
      }
    } catch (e: unknown) {
      setApiError(
        extractErrorMessage(e, 'An error ocurred during data submission.')
      );
    } finally {
      setSaving(false);
    }
  };

  const schemaOptions =
    config.jsonSchema != null && 'options' in config.jsonSchema
      ? config.jsonSchema.options
      : undefined;

  const customWidth = useMemo(
    () =>
      schemaOptions != null &&
      typeof schemaOptions == 'object' &&
      'modalWidth' in schemaOptions &&
      typeof schemaOptions.modalWidth === 'number'
        ? schemaOptions.modalWidth
        : undefined,
    [schemaOptions]
  );

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={() => cleanupAndClose(isDirty)}
            disabled={!isInitial}
          >
            {t('app.createRow.cancel')}
          </Button>
          {!isInitial ? (
            <Button
              icon={
                isPending ? (
                  <SVGIcon name="upload" />
                ) : (
                  <GoogleFontIcon name="check" />
                )
              }
              onClick={() => cleanupAndClose(isDirty)}
              disabled={isPending}
              loading={isSaving}
            >
              {isPending
                ? t('app.createRow.submitting')
                : t('app.createRow.okay')}
            </Button>
          ) : (
            <Button
              icon={<SVGIcon name="upload" />}
              onClick={onSubmit}
              disabled={
                (config.allowFileUpload && !fileSelection[0]) ||
                (config.jsonSchema && validationErrors.length > 0)
              }
              loading={isSaving}
            >
              {t('app.createRow.submit')}
            </Button>
          )}
        </>
      }
      modalType="action"
      opened={opened}
      onClose={() => cleanupAndClose(isDirty)}
      size={pixelSizeHelperPortalUi(customWidth || 650)}
      title={config.buttonLabel ?? t('app.createRow.title')}
    >
      <div
        className={clsx(
          styles['content'],
          'portal-form rje-form rje-theme rje-theme--light'
        )}
      >
        {config.allowFileUpload && (
          <>
            <Upload
              fileState={fileSelection}
              dispatch={dispatch}
              accept={
                config.mimeTypes == null ||
                (config.mimeTypes != null && config.mimeTypes.length === 0)
                  ? undefined
                  : config.mimeTypes
              }
            />
            {fileSelection.length === 0 && (
              <ErrorMessage className={styles['input-section__upload-error']}>
                {t('app.createRow.fileUploadRequired')}
              </ErrorMessage>
            )}
          </>
        )}
        {config.jsonSchema && <Widget editor={editor} />}
        {apiError != null && (
          <ErrorMessage className={styles['input-section__api-error']}>
            {apiError}
          </ErrorMessage>
        )}
      </div>
    </Modal>
  );
}
